import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useBridge } from '../hooks/useBridge';
import { useBaseParam } from '../hooks/useBaseParam';
import { putLoanPreRequest } from '../../infra/api';
import { getLoanPreRequestCheck } from '../../infra/api/getLoanPreRequestCheck';
import useNav from '../hooks/useNav';
import { useRecoilValue } from 'recoil';
import useInterval from '../hooks/useInterval';
import { beaconSelector } from '../recoil/selector';
import useBeacon from '../hooks/useBeacon';
import { PAGE_NM } from '../types/types';
import useBlocker from '../hooks/useBlocker';
import { NOTI_PRE_REG } from '../../application/staticData/info';
import moment from 'moment';

const useLoading = () => {
  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.LOADING,
  });
  useEffect(() => {
    beacon.sendViewPage();
  }, []);
  const location = useLocation();
  const loanInfo: any = location?.state;
  const { navigate } = useNav();

  const br: any = useBridge();
  const closeWebview = () => {
    br.close();
  };

  const [serverSearchTime, setServerSearchTime] = useState<string>('');
  const [preRequestCheckCount, setPreRequestCheckCount] = useState<number>(0);

  useEffect(() => {
    if (loanInfo?.regDate) {
      setServerSearchTime(moment(loanInfo?.regDate).format('YYYYMMDDHHmmss'));
      setPreRequestCheckCount((prev) => prev + 1);
      const diff = moment().diff(moment(loanInfo?.regDate), 'seconds');
      if (diff <= 70) {
        setCountState({ count: diff, status: 'ready' });
      }
    }
  }, [loanInfo?.regDate]);

  const [popup, setPopup] = useState({
    message: '조금만 기다리면 결과를 볼 수 있어요.\n알림으로 받아보시겠어요?',
    description: '결과가 나오면 알림으로 알려드려요.',
    open: false,
    cancelText: '아니요',
    onClickCancel: () => setPopup((prev) => ({ ...prev, open: false })),
    confirmText: '예',
    onClickConfirm: () => closeWebview(),
  });

  const [countState, setCountState] = useState<{
    count: number;
    status: 'ready' | 'take1' | 'take2' | 'take3' | 'done';
  }>({
    count: 0,
    status: 'ready',
  });

  const clearCountStateInterval = useInterval(() => {
    if (countState.status === 'done') return;
    if (countState.count < 70) {
      setCountState((prev) => ({ ...prev, count: prev.count + 1 }));
    }
  }, 1000);

  useEffect(() => {
    if (countState.status === 'done') clearCountStateInterval();
  }, [countState.status]);

  useEffect(() => {
    if (countState.count > 0 && countState.count <= 4) {
      setCountState((prev) => ({ ...prev, status: 'take1' }));
    } else if (countState.count > 4 && countState.count < 70) {
      if (Math.floor(countState.count / 5) % 2) {
        setCountState((prev) => ({ ...prev, status: 'take2' }));
      } else {
        setCountState((prev) => ({ ...prev, status: 'take3' }));
      }
    } else if (countState.count >= 70) {
      setCountState((prev) => ({ ...prev, status: 'done' }));
    }
  }, [countState.count]);
  useBlocker((tx: any) => {
    if (tx.action === 'POP') {
      setPopup((prev) => ({ ...prev, open: true }));
    } else {
      tx.retry();
    }
  }, true);

  const mutateLoanPreRequest = useBaseParam(putLoanPreRequest, {});
  const fetchPreRequestCheck = useBaseParam(
    getLoanPreRequestCheck,
    {
      serverSearchTime: serverSearchTime,
    },
    { noToast: true },
  );

  const navigateForward = () => {
    setCountState((prev) => ({
      ...prev,
      status: 'done',
    }));

    setTimeout(() => {
      navigate('/result', { state: { before: 'loading' }, replace: true });
    }, 3000);
  };

  useEffect(() => {
    if (preRequestCheckCount === 0) return;

    (async () => {
      const result = await fetchPreRequestCheck();

      if (result?.data?.isSucceed === true) {
        // 최초 한 번만 api가 시작된 시간을 담아준다.
        if (serverSearchTime === '')
          setServerSearchTime(result?.data?.result?.serverSearchTime);

        if (result?.data?.result?.result === 'RETRY') {
          setTimeout(() => {
            setPreRequestCheckCount((prev) => prev + 1);
          }, 2000);
        } else if (
          result?.data?.result?.result === 'SUCCESS' ||
          result?.data?.result?.result === 'EMPTY_RESULT' // EMPTY_RESULT 당일 가심사 성공건수가 없고 전일 노출되는 상품이 있는 경우
        ) {
          navigateForward();
        }
      } else if (result?.data?.isSucceed === false) {
        navigateForward();
      }
    })();
  }, [preRequestCheckCount]);

  useEffect(() => {
    if (!loanInfo?.regDate) {
      (async () => {
        const result = await mutateLoanPreRequest();
        if (result?.data?.isSucceed) {
          setPreRequestCheckCount((prev) => prev + 1);
        } else {
          setCountState((prev) => ({ ...prev, status: 'done' }));
        }
      })();
    }
  }, []);

  const getTitleContent = () => {
    let title = '';
    switch (countState.status) {
      case 'ready':
        title = '오버테이크가 금융사에서\n 대출 심사를 진행 중이에요';
        break;
      case 'take1':
        title = '오버테이크가 금융사에서\n 대출 심사를 진행 중이에요';
        break;
      case 'take2':
        title = '신용 조회 알림이 오는 건 \n 정상적으로 진행 중이란 의미예요';
        break;
      case 'take3':
        title = '대출 심사를 진행해도\n 신용점수에 영향을 주지 않아요';
        break;
      case 'done':
        title = '조금만 기다려주세요\n 대출 심사가 거의 다 끝나가요';
        break;
    }

    return {
      title,
      subTitle: `${70 - countState.count}초 보다 일찍 끝날 수 있어요`,
    };
  };

  return {
    popup,
    titleContent: getTitleContent(),
    NOTI_PRE_REG,
  };
};

export default useLoading;
